import type { DevelopmentRole, VisibilityTier, Workstream } from '@/shared/types/projectData';

export const WORKSTREAM_OPTIONS: Workstream[] = ['strategy', 'research', 'build', 'review', 'delivery', 'ops', 'admin'];
export const LEGACY_WORKSTREAM_OPTIONS: Workstream[] = ['development', 'meeting', 'sales', 'general', 'planning'];

export const WORKSTREAM_VALUES = new Set<string>([...WORKSTREAM_OPTIONS, ...LEGACY_WORKSTREAM_OPTIONS]);

export const DEVELOPMENT_ROLE_OPTIONS: DevelopmentRole[] = [
  'architecture',
  'product',
  'planning',
  'ux',
  'ui',
  'frontend',
  'backend',
  'mcp',
  'fullstack',
  'mobile',
  'qa',
  'devops',
  'data',
  'ai',
  'ai_ml',
  'visual',
  'writing',
  'security',
  'integration',
];

export const CANONICAL_DEVELOPMENT_ROLE_OPTIONS: DevelopmentRole[] = [
  'architecture',
  'product',
  'frontend',
  'backend',
  'mcp',
  'qa',
  'data',
  'ai',
  'visual',
  'writing',
  'security',
];

export const PHASE_RECOMMENDED_DEVELOPMENT_ROLES: Partial<Record<Workstream, DevelopmentRole[]>> = {
  strategy: ['product', 'architecture', 'writing', 'qa'],
  research: ['writing', 'data', 'ai', 'visual', 'qa'],
  build: ['product', 'architecture', 'frontend', 'backend', 'mcp', 'data', 'ai', 'visual'],
  review: ['qa', 'security', 'writing', 'visual', 'product', 'architecture', 'backend', 'mcp', 'data'],
  delivery: ['writing', 'visual', 'product', 'qa'],
  ops: ['product', 'mcp', 'writing', 'qa'],
  admin: ['writing', 'security', 'mcp', 'qa'],
};

export const DEFAULT_DEVELOPMENT_ROLES: DevelopmentRole[] = ['architecture'];

export const DEVELOPMENT_ROLE_VALUES = new Set<string>(DEVELOPMENT_ROLE_OPTIONS);

export const VISIBILITY_TIER_OPTIONS: VisibilityTier[] = ['internal', 'partner', 'client'];

export const normalizeWorkstream = (value: unknown): Workstream | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const cleaned = value.trim().toLowerCase();
  const alias = ({
    planning: 'strategy',
    general: 'strategy',
    development: 'build',
    meeting: 'ops',
    sales: 'delivery',
  } as Record<string, Workstream>)[cleaned];
  if (alias) {
    return alias;
  }
  return WORKSTREAM_VALUES.has(cleaned) ? cleaned as Workstream : null;
};

export const normalizeDevelopmentRoles = (value: unknown): DevelopmentRole[] => {
  if (value === null || value === undefined) {
    return [];
  }

  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [value];
  const roles: DevelopmentRole[] = [];
  const seen = new Set<string>();

  rawValues.forEach((raw) => {
    const rawCleaned = String(raw || '').trim().toLowerCase();
    const cleaned = ({
      planning: 'architecture',
      ux: 'product',
      ui: 'frontend',
      integration: 'mcp',
      ai_ml: 'ai',
      fullstack: 'backend',
      mobile: 'frontend',
      devops: 'backend',
    } as Record<string, DevelopmentRole>)[rawCleaned] || rawCleaned;
    if (!cleaned || seen.has(cleaned) || !DEVELOPMENT_ROLE_VALUES.has(cleaned)) {
      return;
    }
    seen.add(cleaned);
    roles.push(cleaned as DevelopmentRole);
  });

  return roles;
};
